/**
 * Time component displays a time of day.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {string} props.value - Time value in the format `HH:MM:SS`.
 * @param {boolean} [props.displaySeconds=false] - Whether to display seconds.
 * @param {string|JSX.Element} [props.fallback='\u2014'] - Fallback string or component to display if value is invalid or `00:00:00`.
 * @returns {JSX.Element} Rendered time or fallback.
 *
 * @example
 * <Time value="13:45:30" displaySeconds={true} />
 * // Renders: 13:45:30
 *
 * <Time value="13:45:30" />
 * // Renders: 13:45
 *
 * <Time value="00:00:00" />
 * // Renders: —
 *
 * <Time value="00:00:00" fallback="N/A" />
 * // Renders: N/A
 *
 * // Using a component as fallback
 * <Time value="00:00:00" fallback={<span style={{color: 'red'}}>Missing</span>} />
 * // Renders: <span style="color: red;">Missing</span>
 *
 */

const Time = ({
  value,
  displaySeconds = false,
  fallback = <span className="time empty">—</span>
}) => {
  const timeValue = value ? String(value) : '';

  // Checks if time is in HH:MM:SS format and not 00:00:00
  if (!/^\d\d:\d\d:\d\d/.test(timeValue) || timeValue === '00:00:00') {
    return fallback;
  }

  // Display time with or without seconds based on displaySeconds prop
  const displayValue = displaySeconds ? timeValue : timeValue.substr(0, 5);
  return <span className="time">{displayValue}</span>;
};

export default Time;
